import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core'
import { FilterService } from '@progress/kendo-angular-grid'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator,
} from '@progress/kendo-data-query'

import { BaseCustomFilterComponent } from './base-custom-filter.component'

@Component({
  standalone: false,
  selector: 'date-range-filter',
  styles: [
    `
      kendo-daterange > kendo-dateinput.range-filter {
        display: inline-block;
      }
      .k-button {
        margin-left: 5px;
      }
    `,
  ],
  template: `
    <kendo-daterange>
      <kendo-dateinput
        class="range-filter"
        kendoDateRangeStartInput
        [value]="startValue"
        (valueChange)="filterRange($event, endValue)"
        style="width:80px"
      >
      </kendo-dateinput>
      -
      <kendo-dateinput
        class="range-filter"
        kendoDateRangeEndInput
        [value]="endValue"
        (valueChange)="filterRange(startValue, $event)"
        style="width:80px"
      >
      </kendo-dateinput>
    </kendo-daterange>
    @if (hasFilter) {
      <button
        kendoButton
        icon="clear"
        title="Clear"
        (click)="clearFilter()"
      ></button>
    }
  `,
})
export class DateRangeFilterComponent
  extends BaseCustomFilterComponent
  implements OnChanges
{
  @Input() public filter: CompositeFilterDescriptor
  @Input() public field: string

  public startValue: Date | null = null
  public endValue: Date | null = null

  // Constructor needed for proper filter functioning

  constructor() {
    const filterService = inject(FilterService)

    super(filterService)
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['field']) {
      const newStart = this.getDateFromFilter(FilterOperator.GreaterThanOrEqual)
      const newEnd = this.getDateFromFilter(FilterOperator.LessThanOrEqual)

      // Only replace the reference when the date value actually changed so
      // Kendo's DateInput doesn't see a new object on every CD cycle, which
      // would re-emit valueChange and cause an infinite filter → API loop.
      if (!datesEqual(this.startValue, newStart)) this.startValue = newStart
      if (!datesEqual(this.endValue, newEnd)) this.endValue = newEnd
    }
  }

  public resetDefault(): void {
    this.clearFilter()
  }

  public get hasFilter(): boolean {
    return this.filtersByField(this.field).length > 0
  }

  public clearFilter(): void {
    this.startValue = null
    this.endValue = null
    this.filterService.filter(this.removeFilter(this.field))
  }

  public addFilter(filter: FilterDescriptor): void {
    const root = this.filter || { logic: 'and', filters: [] }
    root.filters.push({
      field: filter.field,
      operator: filter.operator,
      value: new Date(filter.value),
    })
    this.filterService.filter(root)
  }

  public filterRange(start: Date, end: Date): void {
    // Update synchronously so [value] binding reflects the selection before
    // Angular's next CD cycle runs. Without this, CD would push [value]=null
    // back to Kendo (startValue not yet updated by ngOnChanges), resetting the
    // input and firing valueChange(null), which immediately clears the filter.
    this.startValue = start
    this.endValue = end

    const root = this.removeFilter(this.field) || {
      logic: 'and' as const,
      filters: [],
    }

    const filters: FilterDescriptor[] = []

    if (start) {
      filters.push({
        field: this.field,
        operator: FilterOperator.GreaterThanOrEqual,
        value: start,
      })
    }

    if (end) {
      filters.push({
        field: this.field,
        operator: FilterOperator.LessThanOrEqual,
        value: end,
      })
    }

    if (filters.length) {
      root.filters.push(...filters)
    }

    this.filterService.filter(root)
  }

  private getDateFromFilter(operator: FilterOperator): Date | null {
    if (this.filter && this.filter.filters) {
      const match = (this.filter.filters as FilterDescriptor[]).find(
        ({ field, operator: op }) => field === this.field && op === operator,
      )
      if (match && match.value) return new Date(match.value)
    }
    return null
  }
}

function datesEqual(a: Date | null, b: Date | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.getTime() === b.getTime()
}
